import { FormsFactory } from './forms/forms-factory';
import { Widget } from './widgets/widget';
import { Config } from './config';
import { Form } from './forms/form';
import { FormTypes } from './forms/form-types';
import { DepositfixProvider } from './providers/depositfix-provider';
import { View } from './view/view';
import { Subtotal } from './config/subtotal';
import { Translations } from './config/translations';
import { PaymentType } from './providers/payment-type';
import { Product } from './config/product';
import { ProductType } from './config/product-type';
import { Util } from './config/utils';
import { ErrorView } from './view/error-view';
import { ErrorType } from './config/error-type';
import { DeviceType } from './config/device-type';
import { DepositFixAPI } from './api';
import { currencySymbols } from './config/currency-symbols';

declare const window: any;
declare const process: any;

export class DepositFix {

    private provider: DepositfixProvider = new DepositfixProvider();
    private _widget: Widget = {} as Widget;
    private _view: View = new View();
    private errorView: ErrorView = new ErrorView();
    private config: Config = {};
    private _api: DepositFixAPI = new DepositFixAPI(this);
    private _now = new Date().getTime();

    init(params: any) {
        if (Util.isDfScriptV2() && params.iframe) {
            this.initIFrame(params);
            return;
        }

        this._view.renderFormLoader();

        if (!Util.isLocationProtocolSecured(location.protocol)) {
            this.errorView.render(ErrorType.PROTOCOL_NOT_SECURED);
            return;
        }
        if (Util.isBrowserInternetExplorer()) {
            Util.loadInternetExplorerSupportScript(process.env.INTERNET_EXPLORER_SUPPORT_FILE, () => this.initConfig(params));
            return;
        }
        return Promise.resolve(this.initConfig(params));
    }

    initIFrame(params: any) {
        params.iframe = false;

        const iFrameContainer = document.createElement('iframe');
        iFrameContainer.id = 'df-iframe';
        iFrameContainer.style.border = 'none';
        iFrameContainer.style['max-width'] = '100%';
        iFrameContainer.style['width'] = '550px';
        iFrameContainer.style.height = '50px';

        const dfScript = document.getElementById('df-script');
        const dfWidgetScript = document.getElementById('df-widget-js');

        if (dfScript && dfScript.parentNode) {
            dfScript.parentNode.insertBefore(iFrameContainer, dfScript.nextSibling);
        } else {
            document.body.append(iFrameContainer);
        }

        iFrameContainer.contentWindow?.document.open();
        iFrameContainer.contentWindow?.document.write(this.getDfIFrameHtml(params, dfWidgetScript));
        iFrameContainer.contentWindow?.document.close();
    }

    getDfIFrameHtml(params: any, dfWidgetScript) {
        const iFrameParams = JSON.stringify(params, (key, value) => typeof value === 'function' ? value.toString() : value);

        return `<html>
                    <body>
                        <script id="df-widget-js" src="${dfWidgetScript?.getAttribute('src')}"></script>
                        <script id="df-script" type="text/javascript">
                            DepositFixForm.init(${iFrameParams});
                            DepositFixForm.iFrameFormReady = function() {
                                let iFrameElement = window.parent.document.getElementById("df-iframe");
                                if (iFrameElement) {
                                    iFrameElement.style.height = "";
                                    iFrameElement.style.height = (document.body.scrollHeight + 100) + "px";
                                }
                                window.top['DepositFixForm'] = window['DepositFixForm'];
                            };
                        </script>
                    </body>
                </html>`;
    }

    async initConfig(params: any) {
        let config: Config;
        const propsCallbacks: any = {};
        const responseCallbacks: any = {};
        if (params.callbacks) {
            const isDfFormRenderedAsIFrame = Util.isDfFormIFrame();
            for (const prop in params.callbacks) {
                if (isDfFormRenderedAsIFrame) {
                    propsCallbacks[prop] = Util.parseFunction(params.callbacks[prop]);
                } else if (typeof params.callbacks[prop] === 'function') {
                    propsCallbacks[prop] = params.callbacks[prop];
                }
            }
        }
        if (params && !params.formId) {
            params.user = {
                portalId: params.portalId,
                hubSpotPortalId: params.hubSpotPortalId,
                userActive: true
            };
        }

        const configResponse = params && params.portalId && params.formId ?
            this.getConfig(params) : Promise.resolve(params);

        await configResponse
            .then((response: Config): any => {

                if (!response) {
                    return Promise.reject();
                }

                if (response.user && !response.user.userActive) {
                    return this.errorView.render(ErrorType.ACCOUNT_HAS_EXPIRED)
                        .then(() => Promise.reject());
                }

                for (const prop in response.settings.callbacks) {
                    if (typeof response.settings.callbacks[prop] === 'string') {
                        // tslint:disable-next-line
                        responseCallbacks[prop] = new Function("data", response.settings.callbacks[prop]);
                    } else {
                        responseCallbacks[prop] = response.settings.callbacks[prop];
                    }
                }

                config = this.mergeConfigs(response, params);

                config.callbacks = {
                    formCallbacks: responseCallbacks,
                    inlineCallbacks: propsCallbacks
                };

                if (response.settings && response.settings.translations) {
                    this.applyTranslations(response.settings.translations);
                }

                if (params.translations) {
                    this.applyTranslations(params.translations);
                }

                if (params.isLogging) {
                    config.isLogging = params.isLogging;
                }

                config.products = this.mapToProducts(config);

                config.currencySymbols = currencySymbols;

                this.config = config;
            })
            .then(() => this.resolveOnInit(config.onInit, config))
            .then(() => this.initWidget(config))
            .catch(() => {
                this._view.removeFormLoader();
                return Promise.reject();
            });
    }

    getConfig = (params: any) => {
        return this.provider.getConfig(params, Util.isForcedTestMode());
    }

    mapToProducts(config: any): Product[] {
        let products: Product[] = [];
        if (!config.products) {
            return [];
        }
        products = config.products.map((product: any) => {
            const productModel: any = {
                dfProductId: product.dfProductId,
                id: product.id,
                type: product.type,
                name: product.name,
                amount: product.amount,
                interval: product.interval,
                showQuantity: product.showQuantity,
                maxCharges: product.maxCharges,
                addon: product.addon,
                frequency: product.frequency,
                defaultFrequency: product.defaultFrequency,
                prices: product.prices,
                subscriptionPrices: product.subscriptionPrices,
                amounts: product.amounts,
                step: product.step
            };

            if (product.type === 'donation') {
                productModel.minimumDonation = product.minimumDonation;
            }
            return productModel;
        });
        return products;
    }

    getUrlParameter(name: any, url: any) {
        return Util.getUrlParameter(name, url);
    }

    mergeConfigs(response: any, params: any) {

        if (!response.settings || !response.settings.currency) {
            response.settings.currency = 'USD';
        }

        if (params.settings) {
            response.settings = Object.assign(response.settings || {}, params.settings);
        }

        if (response.settings.cssFile && response.settings.cssFile === 'false') {
            response.settings.cssFile = false;
        }

        if (params.user) {
            response.user = Object.assign(response.user || {}, params.user);
        }
        if (params.products) {
            response.products = params.products;
        }
        if (params.forms) {
            response.forms = Object.assign(response.forms || {}, params.forms);
        }
        if (params.callbacks) {
            response.callbacks = Object.assign(response.callbacks || {}, params.callbacks);
        }
        if (params.formId) {
            response.formId = params.formId;
        }
        const legacyDonationIntervals = params.legacyDonationIntervals;
        if (legacyDonationIntervals && legacyDonationIntervals !== null && legacyDonationIntervals !== undefined && legacyDonationIntervals === true) {
            response.legacyDonationIntervals = true;
        }

        if (params.customDealProperties) {
            response.customDealProperties = params.customDealProperties;
        }

        return response;
    }

    applyTranslations(translations: any) {
        for (const label in translations) {
            // @ts-ignore
            Translations[label.toUpperCase()] = translations[label];
        }
    }

    private async initWidget(config: Config) {
        const forms = this.parseFormsConfig(config);
        this._widget = new Widget(forms);
        await this._widget.init(config);
        await this._widget.render();
        this._widget.triggerAppEvent('onFormReady');
        this._view.removeFormLoader();
        this.iFrameFormReady();
    }

    iFrameFormReady() {}

    async ready() {
        await new Promise<void>(resolve => window.onload = () => resolve());
    }

    private parseFormsConfig(config: Config): Form[] {
        config = Object.assign(config, { paymentMethod: [] });
        const forms: Form[] = [];
        if (config.forms) {
            if (config.forms.hubspot && config.forms.hubspot.id) {
                forms.push(FormsFactory.createForm(FormTypes.Hubspot, config.forms.hubspot.step));
            }
        }
        if (config.settings.discount || config.settings.stripeDiscount) {
            forms.push(FormsFactory.createForm(FormTypes.Discount, config.settings.discountStep));
        }

        if (config.products) {
            const checkDynamicSubscription = config.products.find((product: Product) => product.type === ProductType.DYNAMIC_SUBSCRIPTION);
            if (checkDynamicSubscription) {
                forms.push(FormsFactory.createForm(FormTypes.DynamicSubscription, checkDynamicSubscription.step));
            }

            const checkDonation = config.products.find((product: Product) => product.type === ProductType.DONATION);
            if (checkDonation) {
                forms.push(FormsFactory.createForm(FormTypes.DonationForm, checkDonation.step));
            }

            const checkAdaptivePrices = config.products.find((product: Product) => product.prices);
            if (checkAdaptivePrices) {
                forms.push(FormsFactory.createForm(FormTypes.AdaptivePrices, checkAdaptivePrices.step));
            }

            const checkAdaptiveSubscriptionPrices = config.products.find((product: Product) => product.subscriptionPrices);
            if (checkAdaptiveSubscriptionPrices) {
                forms.push(FormsFactory.createForm(FormTypes.AdaptiveSubscriptionPrices, checkAdaptiveSubscriptionPrices.step));
            }

            const checkSubscriptions = config.products.find((product: Product) => (product.type === ProductType.SUBSCRIPTION) &&
                !product.prices && !product.subscriptionPrices && !product.addon);
            if (checkSubscriptions) {
                forms.push(FormsFactory.createForm(FormTypes.Subscription, checkSubscriptions.step));
            }

            const checkSingleProducts = config.products.find((product: Product) => (product.type === ProductType.SINGLE_PAYMENT || product.type === ProductType.NO_PAYMENT) &&
                !product.prices && !product.subscriptionPrices && !product.addon);
            if (checkSingleProducts) {
                forms.push(FormsFactory.createForm(FormTypes.Product, checkSingleProducts.step));
            }

            const checkSingleProductAddon = config.products.find((product: Product) => product.addon && (product.type === ProductType.SINGLE_PAYMENT ||
                product.type === ProductType.SUBSCRIPTION || product.type === ProductType.NO_PAYMENT) &&
                !product.prices && !product.subscriptionPrices);
            if (checkSingleProductAddon) {
                forms.push(FormsFactory.createForm(FormTypes.Addon, checkSingleProductAddon.step));
            }

            if (config.settings.multipleContacts) {
                forms.push(FormsFactory.createForm(FormTypes.MultipleContacts));
            }

            if (config.settings.autoSalesTax) {
                forms.push(FormsFactory.createForm(FormTypes.Tax));
            }
        }

        if (config.analytics) {
            if (config.analytics.facebookPixelId) {
                forms.push(FormsFactory.createForm(FormTypes.FacebookPixel));
            }
            if (config.analytics.googleAnalyticsId) {
                forms.push(FormsFactory.createForm(FormTypes.GoogleAnalytics));
            }
        }

        if (config.forms) {
            const isStripeEnabled = config.forms.stripe && config.forms.stripe.stripeKey;
            if (isStripeEnabled && Util.getDeviceType() === DeviceType.ANDROID && config.forms.stripe.isStripeGooglePayEnabled) {
                config.paymentMethod.push(PaymentType.STRIPE_GOOGLE_PAY);
                forms.push(FormsFactory.createForm(FormTypes.Stripe_Google_Pay, config.settings.paymentStep));
            }
            if (isStripeEnabled && Util.getDeviceType() === DeviceType.IPHONE && config.forms.stripe.isStripeApplePayEnabled) {
                config.paymentMethod.push(PaymentType.STRIPE_APPLE_PAY);
                forms.push(FormsFactory.createForm(FormTypes.Stripe_Apple_Pay, config.settings.paymentStep));
            }
            if (isStripeEnabled) {
                config.paymentMethod.push(PaymentType.STRIPE);
                forms.push(FormsFactory.createForm(FormTypes.Stripe, config.settings.paymentStep));
            }
            if (isStripeEnabled && config.forms.stripe.ach) {
                config.paymentMethod.push(PaymentType.STRIPE_ACH);
                forms.push(FormsFactory.createForm(FormTypes.Stripe_Ach, config.settings.paymentStep));
            }
            if (isStripeEnabled && config.forms.stripe.isStripeBacsDebitEnabled && config.settings.currency === 'GBP') {
                config.paymentMethod.push(PaymentType.STRIPE_BACS);
                forms.push(FormsFactory.createForm(FormTypes.Stripe_Bacs, config.settings.paymentStep));
            }
            if (config.forms.paypal && config.forms.paypal.email) {
                config.paymentMethod.push(PaymentType.PAYPAL);
                forms.push(FormsFactory.createForm(FormTypes.Paypal, config.settings.paymentStep));
            }
            if (config.forms.offlinePayment) {
                config.paymentMethod.push(PaymentType.OFFLINE);
                forms.push(FormsFactory.createForm(FormTypes.OfflinePayment, config.settings.paymentStep));
            }
        }

        if (config.recaptchaScoreEnabled && config.recaptchaScoreEnabled === true) {
            forms.push(FormsFactory.createForm(FormTypes.RecaptchaV3));
        }
        if (config.recaptchaCheckboxEnabled && config.recaptchaCheckboxEnabled === true) {
            forms.push(FormsFactory.createForm(FormTypes.RecaptchaV2));
        }

        if (config.settings.countdown && config.settings.countdown.seconds) {
            forms.push(FormsFactory.createForm(FormTypes.CountdownTimer));
        }

        // Do not use for clients ! Bellow are only for js logging purpose.
        if (config.isLogging && config.isLogging.onInit) {
            forms.push(FormsFactory.createForm(FormTypes.LogFailingOnInit));
        }
        if (config.isLogging && config.isLogging.onRender) {
            forms.push(FormsFactory.createForm(FormTypes.LogFailingOnRender));
        }

        return forms;
    }

    private lifeCycleHooksApi(config: Config) {
        return {
            setAmount: (amount: number) => Object.assign(config.products[0] || {}, {
                amount
            }),

            setPaymentType: (paymentType: string) => Object.assign(config.settings, {
                paymentType
            })
        };
    }

    resolveOnInit(initCallback: Function, config: Config): Promise<any> {
        if (initCallback) {
            const hookApi = this.lifeCycleHooksApi(config);
            if (initCallback instanceof Promise) {
                return initCallback(hookApi);
            }

            return Promise.resolve(initCallback(hookApi));
        }

        return Promise.resolve();
    }


    setTotalAmount(value: number) {
        this._widget.setExternalAmount(value);
        this._widget.view.setChangedAmount(new Subtotal(), this.config.settings.currency, value.toString(), []);
    }

    getTotal() {
        return this._widget.view.getTotal();
    }

    getProducts() {
        if (this.config.products) {
            return this.config.products;
        }
    }

    get widget(): Widget {
        return this._widget;
    }

    get view(): View {
        return this._view;
    }

    get api(): DepositFixAPI {
        return this._api;
    }

    set api(value: DepositFixAPI) {
        this._api = value;
    }

}

window['DepositFixForm'] = new DepositFix();
